"use client";
import React, { useState, useEffect, useMemo } from "react";
import AboutHeroSection from "./AboutHeroSection";
import JobCard from "./JobCard";
import "./Career.css";
import careerImg from "../../public/assets/images/career/career.png";
import { jobAPI } from "@/services/pricing";
import ApplicationForm from "./ApplicationForm ";
import SuccessMessage from "./SuccessMessage";

const Career = ({ jobData = [], loading, error }) => {
  const [jobs, setJobs] = useState([]);
  // const [loading, setLoading] = useState(true);
  // const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");
  const [showModal, setShowModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [applicationError, setApplicationError] = useState(null);
  const [resumeFile, setResumeFile] = useState(null);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    city: "",
    country: "",
    totalExperienceYears: "",
    skills: "",
    currentCompany: "",
    currentTitle: "",
    expectedCTC: "",
    currentCTC: "",
    earliestStartDate: "",
    resume: null,
    privacyConsent: false,
    customAnswers: {},
  });

  useEffect(() => {
    if (!jobData || !jobData instanceof Array || !jobData.length > 0) return;
    setJobs(jobData);
  }, [jobData]);

  const loadingState = useMemo(() => {
    return loading;
  }, [loading]);

  const errorState = useMemo(() => {
    return error;
  }, [error]);

  // const fetchJobs = async () => {
  //   setLoading(true);
  //   const { data, error } = await jobAPI.fetchJobs();
  //   setJobs(data);
  //   setError(error);
  //   setLoading(false);
  // };

  const filteredJobs = jobs.filter((job) => {
    if (filter === "all") return !job.isDeleted;
    if (filter === "open") return job.isOpen && !job.isDeleted;
    if (filter === "closed") return !job.isOpen && !job.isDeleted;
    return true;
  });

  const resetForm = () => {
    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      city: "",
      country: "",
      totalExperienceYears: "",
      skills: "",
      currentCompany: "",
      currentTitle: "",
      expectedCTC: "",
      currentCTC: "",
      earliestStartDate: "",
      resume: null,
      privacyConsent: false,
      customAnswers: {},
    });
    setResumeFile(null);
    setApplicationError(null);
  };

  const handleApplyClick = (job) => {
    setSelectedJob(job);
    setShowModal(true);
    setShowSuccess(false);
    setApplicationError(null);

    // Initialize custom answers
    const initialAnswers = {};
    if (job.customQuestions && job.customQuestions.length > 0) {
      job.customQuestions.forEach((question) => {
        initialAnswers[question._id] =
          question.type === "boolean" ? null : "";
      });
    }
    setFormData((prev) => ({
      ...prev,
      customAnswers: initialAnswers,
    }));
    document.body.style.overflow = "hidden";
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedJob(null);
    setShowSuccess(false);
    resetForm();
    document.body.style.overflow = "auto";
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    // Clear error when user starts typing
    if (applicationError) {
      setApplicationError(null);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setResumeFile(file);
    setFormData((prev) => ({
      ...prev,
      resume: file,
    }));
  };

  const handleCustomQuestionChange = (id, value) => {
    setFormData((prev) => ({
      ...prev,
      customAnswers: {
        ...prev.customAnswers,
        [id]: value,
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setApplicationError(null);

    const result = await jobAPI.submitApplication(
      selectedJob._id,
      formData,
      resumeFile
    );

    setSubmitting(false);

    if (result.success) {
      setShowSuccess(true);
      resetForm();
    } else {
      // Check if it's an "already applied" error
      if (
        result.message.toLowerCase().includes("already applied") ||
        result.message.toLowerCase().includes("duplicate")
      ) {
        setApplicationError(
          "You have already applied for this position. Please check your email for updates."
        );
      } else {
        setApplicationError(result.message);
      }
    }
  };

  console.log(loading, error);

  return (
    <div className="career-page">
      <AboutHeroSection
        pillText="~ we're hiring! ~"
        title="Be part of our"
        highlight="mission"
        description="We're looking for passionate people to join us on our mission. We value flat hierarchies, clear communication, and full ownership and responsibility"
        imageAlt="Finest work"
        image={careerImg}
      />

      <div className="career-container">
        <div className="filter-tabs">
          <button
            onClick={() => setFilter("all")}
            className={`filter-tab ${filter === "all" ? "active" : ""}`}
          >
            All Positions ({jobs.filter((j) => !j.isDeleted).length})
          </button>
          <button
            onClick={() => setFilter("open")}
            className={`filter-tab ${filter === "open" ? "active" : ""}`}
          >
            Open ({jobs.filter((j) => j.isOpen && !j.isDeleted).length})
          </button>
          <button
            onClick={() => setFilter("closed")}
            className={`filter-tab ${filter === "closed" ? "active" : ""}`}
          >
            Closed ({jobs.filter((j) => !j.isOpen && !j.isDeleted).length})
          </button>
        </div>

        {loadingState && (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading opportunities...</p>
          </div>
        )}

        {errorState && (
          <div className="error-state">
            <p className="error-title">Failed to load jobs</p>
            <p className="error-message">{errorState}</p>
            <button onClick={fetchJobs} className="retry-button">
              Try Again
            </button>
          </div>
        )}

        {!loadingState && !errorState && (
          <>
            {filteredJobs.length === 0 ? (
              <div className="empty-state">
                <p className="empty-title">
                  No positions available at the moment.
                </p>
                <p className="empty-subtitle">
                  Check back soon for new opportunities!
                </p>
              </div>
            ) : (
              <div className="jobs-grid">
                {filteredJobs.map((job) => (
                  <JobCard
                    key={job._id}
                    job={job}
                    onApplyClick={handleApplyClick}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Application Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="career-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Apply for {selectedJob?.title}</h2>
              <button className="close-button" onClick={closeModal}>
                ×
              </button>
            </div>

            {showSuccess ? (
              <SuccessMessage
                jobTitle={selectedJob?.title}
                onClose={closeModal}
              />
            ) : (
              <>
                {applicationError && (
                  <div className="application-error">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="12" y1="8" x2="12" y2="12"></line>
                      <line x1="12" y1="16" x2="12.01" y2="16"></line>
                    </svg>
                    <p>{applicationError}</p>
                  </div>
                )}

                <ApplicationForm
                  formData={formData}
                  selectedJob={selectedJob}
                  resumeFile={resumeFile}
                  submitting={submitting}
                  onInputChange={handleInputChange}
                  onFileChange={handleFileChange}
                  onCustomQuestionChange={handleCustomQuestionChange}
                  onSubmit={handleSubmit}
                  onCancel={closeModal}
                />
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Career;